import { Avatar } from './Avatar';
import { InstitutionLink } from './InstitutionLink';
import { EmailDetail } from './EmailDetail';
import { PhoneDetail } from './PhoneDetail';
import './PersonDetailHeader.scss';

interface Institution {
  id: string;
  name?: string;
  type?: string;
  access?: string;
  loc_state?: string;
}

interface BelongsTo {
  institution?: Institution;
}

interface PersonDetailHeaderProps {
  person: {
    id?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    title?: string;
    email?: string;
    emailType?: string | null;
    emailFormat?: string | null;
    emailSource?: string | null;
    emailVerificationStatus?: string | null;
    emailVerifiedAt?: string | null;
    phone?: string;
    phoneUniqueness?: string | null;
    belongsTo?: BelongsTo[];
  };
  alternativeNames?: string[];
  actionButton?: React.ReactNode;
}

export function PersonDetailHeader({ person, alternativeNames = [], actionButton }: PersonDetailHeaderProps) {
  const displayName = person.name || `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unknown';
  const displayTitle = person.title;

  // Build tooltip content for alternative names
  const altNamesTooltip = alternativeNames.length > 0
    ? `Also observed as:<br/>${alternativeNames.map(n => `• ${n}`).join('<br/>')}`
    : '';

  // Get institutions from belongsTo array, sorted by importance
  const typeOrder: Record<string, number> = {
    'AGENCY': 0,
    'DISTRICT': 1,
    'SCHOOL': 2,
  };
  const institutions = (person.belongsTo
    ?.map(b => b.institution)
    .filter((inst): inst is Institution => !!inst) || [])
    .sort((a, b) => {
      const orderA = typeOrder[a.type || ''] ?? 99;
      const orderB = typeOrder[b.type || ''] ?? 99;
      return orderA - orderB;
    });

  return (
    <div className="person-detail-header">
      <Avatar name={displayName} id={person.id} />
      <div className="person-detail-header__content">
        <div className="person-detail-header__name-row">
          <div className="person-detail-header__name">
            {displayName}
            {alternativeNames.length > 0 && (
              <span
                className="person-detail-header__alt-names"
                data-tooltip-id="main-tooltip"
                data-tooltip-html={altNamesTooltip}
              >
                +{alternativeNames.length} name{alternativeNames.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          {actionButton && (
            <div className="person-detail-header__action">
              {actionButton}
            </div>
          )}
        </div>
        {displayTitle && <div className="person-detail-header__title">{displayTitle}</div>}
        <div className="person-detail-header__institutions">
          {institutions.map((inst) => (
            <InstitutionLink
              key={inst.id}
              id={inst.id}
              name={inst.name}
              access={inst.access}
              state={inst.loc_state}
            />
          ))}
        </div>
        {person.email && (
          <div className="person-detail-header__email">
            <EmailDetail
              email={person.email}
              emailType={person.emailType}
              emailFormat={person.emailFormat}
              emailSource={person.emailSource}
              emailVerificationStatus={person.emailVerificationStatus}
              emailVerifiedAt={person.emailVerifiedAt}
            />
          </div>
        )}
        {person.phone && (
          <div className="person-detail-header__phone">
            <PhoneDetail
              phone={person.phone}
              phoneUniqueness={person.phoneUniqueness}
            />
          </div>
        )}
      </div>
    </div>
  );
}
